import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SmsIcon from "@mui/icons-material/Sms";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import CustomerApplicantContractApi from "../../../../../infrastructure/api/customerApplicantContract";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import OtpCodeInput from "../../../../components/shared/OtpCodeInput";
import {
  ContractDownloadActions,
  ContractOtpDialog,
  ContractStatusCard,
} from "../../shared/contracts/UniversalContractComponents";

const WORKSPACE = {
  NotAvailable: "NotAvailable",
  ReadyToSign: "ReadyToSign",
  PendingSignature: "PendingSignature",
  Signed: "Signed",
  NotSignable: "NotSignable",
};

const normalizeSummary = (raw) => {
  if (!raw) return null;
  return {
    requestId: raw?.requestId ?? raw?.RequestId,
    contractId: raw?.contractId ?? raw?.ContractId ?? null,
    contractNumber: raw?.contractNumber ?? raw?.ContractNumber ?? "",
    projectCode: raw?.projectCode ?? raw?.ProjectCode ?? "",
    status: raw?.status ?? raw?.Status ?? null,
    statusTitle: raw?.statusTitle ?? raw?.StatusTitle ?? "",
    versionNumber: raw?.versionNumber ?? raw?.VersionNumber ?? null,
    pdfSha256: raw?.pdfSha256 ?? raw?.PdfSha256 ?? "",
    verificationCode: raw?.verificationCode ?? raw?.VerificationCode ?? "",
    workspaceState: raw?.workspaceState ?? raw?.WorkspaceState ?? WORKSPACE.NotAvailable,
    canDownloadPdf: raw?.canDownloadPdf ?? raw?.CanDownloadPdf ?? false,
    canSendSignatureOtp: raw?.canSendSignatureOtp ?? raw?.CanSendSignatureOtp ?? false,
    canVerifyAndSign: raw?.canVerifyAndSign ?? raw?.CanVerifyAndSign ?? false,
    isSigned: raw?.isSigned ?? raw?.IsSigned ?? false,
    message: raw?.message ?? raw?.Message ?? "",
  };
};

const statusChipColor = (workspaceState) => {
  switch (workspaceState) {
    case WORKSPACE.Signed:
      return "success";
    case WORKSPACE.PendingSignature:
      return "warning";
    case WORKSPACE.ReadyToSign:
      return "info";
    default:
      return "default";
  }
};

const CustomerApplicantContractSignPage = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [maskedMobile, setMaskedMobile] = useState("");
  const [otpCode, setOtpCode] = useState("");

  const loadSummary = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    try {
      const res = await CustomerApplicantContractApi.getSummary(requestId);
      if (!res.ok) {
        toast.error(res.message || "دسترسی به قرارداد مجاز نیست.");
        setSummary(null);
        return;
      }
      const next = normalizeSummary(res.data);
      setSummary(next);
      if (next?.workspaceState === WORKSPACE.PendingSignature) {
        setOtpSent(true);
      }
      if (next?.isSigned) {
        setOtpSent(false);
        setOtpCode("");
      }
    } catch {
      toast.error("خطا در بارگذاری قرارداد.");
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const handleDownload = async () => {
    if (!summary?.contractId) return;
    setActionLoading(true);
    try {
      const blob = await CustomerApplicantContractApi.downloadPdf(summary.contractId);
      if (!blob) {
        toast.error("دانلود فایل قرارداد ناموفق بود.");
        return;
      }
      const url = window.URL.createObjectURL(
        new Blob([blob], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `applicant-contract-${summary.contractNumber || requestId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "خطا در دانلود قرارداد."
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (!summary?.contractId) return;
    setActionLoading(true);
    try {
      const res = await CustomerApplicantContractApi.sendSignatureOtp(summary.contractId);
      if (!res.ok) {
        toast.error(res.message || "ارسال کد تأیید ناموفق بود.");
        return;
      }
      toast.success(res.message || "کد تأیید ارسال شد.");
      setMaskedMobile(
        res.data?.maskedMobile ?? res.data?.MaskedMobile ?? ""
      );
      setOtpSent(true);
      setOtpCode("");
      await loadSummary();
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerifyAndSign = async () => {
    if (!summary?.contractId) return;
    if (!otpCode || String(otpCode).length < 5) {
      toast.error("کد تأیید ۵ رقمی را وارد کنید.");
      return;
    }
    setActionLoading(true);
    try {
      const res = await CustomerApplicantContractApi.verifyAndSign(
        summary.contractId,
        otpCode
      );
      if (!res.ok) {
        toast.error(res.message || "تأیید امضا ناموفق بود.");
        return;
      }
      toast.success(res.message || "قرارداد با موفقیت امضا شد.");
      setOtpCode("");
      setOtpSent(false);
      await loadSummary();
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (!summary) {
    return (
      <Box p={2}>
        <Alert severity="error">
          امکان مشاهده قرارداد وجود ندارد. فقط مالک درخواست می‌تواند قرارداد را ببیند و امضا
          کند.
        </Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate("/SLN/Customer/Requests")}>
          بازگشت به درخواست‌ها
        </Button>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <BreadcrumbsComponent />

      <Card sx={{ mt: 2 }}>
        <CardHeader
          title="قرارداد متقاضی"
          subheader={summary.message}
          action={
            <Chip
              label={summary.statusTitle || summary.workspaceState}
              color={statusChipColor(summary.workspaceState)}
              variant="outlined"
            />
          }
        />
        <Divider />
        <CardContent>
          <Stack spacing={1.5} mb={3}>
            <ContractStatusCard
              title="وضعیت قرارداد متقاضی"
              contractNumber={summary.contractNumber}
              versionNumber={summary.versionNumber}
              status={summary.status}
              statusTitle={summary.statusTitle || summary.workspaceState}
              projectCode={summary.projectCode}
              verificationCode={summary.verificationCode}
            />
            <Typography variant="body2">شماره درخواست: {summary.requestId}</Typography>
          </Stack>

          {summary.workspaceState === WORKSPACE.NotAvailable ? (
            <Alert severity="info">
              قرارداد هنوز توسط سولارنت تولید نشده است. پس از تولید، از همین صفحه می‌توانید آن را
              مشاهده و امضا کنید.
            </Alert>
          ) : null}

          {summary.isSigned ? (
            <Alert
              severity="success"
              icon={<CheckCircleOutlineIcon fontSize="inherit" />}
              sx={{ mb: 2 }}
            >
              این قرارداد امضا شده است. می‌توانید نسخه PDF را دانلود کنید.
            </Alert>
          ) : null}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mb={3}>
            <ContractDownloadActions
              canDownloadFinal={summary.canDownloadPdf}
              onDownloadFinal={handleDownload}
              loading={actionLoading}
            />
            <Button
              variant="contained"
              startIcon={<SmsIcon />}
              disabled={!summary.canSendSignatureOtp || actionLoading}
              onClick={handleSendOtp}
            >
              ارسال کد تأیید امضا
            </Button>
            <Button
              variant="text"
              onClick={() => navigate("/SLN/Customer/Requests")}
              disabled={actionLoading}
            >
              بازگشت
            </Button>
          </Stack>

          {(otpSent || summary.canVerifyAndSign) && !summary.isSigned ? (
            <ContractOtpDialog
              open
              maskedMobile={maskedMobile}
              otpCode={otpCode}
              onOtpChange={setOtpCode}
              onSubmit={handleVerifyAndSign}
              onCancel={() => {
                setOtpSent(false);
                setOtpCode("");
              }}
              loading={actionLoading}
              OtpInput={({ value, onChange }) => (
                <OtpCodeInput
                  fields={5}
                  value={value}
                  onChange={onChange}
                  disabled={actionLoading}
                />
              )}
            />
          ) : null}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CustomerApplicantContractSignPage;
